import {
    AutorenewOutlined,
    DeleteForeverOutlined,
    Edit,
    EditCalendarOutlined,
    LocalOfferOutlined,
} from "@mui/icons-material";
import { Button, ButtonGroup, Popover, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import TerminateForm from "./TerminateForm";
import EditPlanForm from "./EditPlanForm";
import EditDurationForm from "./EditDurationForm";
import RenewForm from "./RenewForm";
import EditTagsForm from "./EditTagsForm";
import { differenceInCalendarDays, parseISO } from "date-fns";

const SUBSCRIPTION_ACTION_FORMS = {
    RENEW: "renew",
    CHANGE_PLAN: "changePlan",
    CHANGE_DURATION: "changeDuration",
    EDIT_TAGS: "editTags",
    TERMINATE: "terminate",
};

const SubscriptionRowActions = ({
    subscriptionId,
    isActive,
    plan,
    startAt,
    endAt,
    tags,
    setIsNotificationDialogOpen,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeForm, setActiveForm] = useState(null);

    const handleOpen = (event, formType) => {
        setAnchorEl(event.currentTarget);
        setActiveForm(formType);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "subscription-action" : undefined;

    const isFreshlyExpired = () => {
        if (!endAt) return false;
        const today = new Date();
        const endDate =
            typeof endAt === "string" ? parseISO(endAt) : new Date(endAt);

        const daysDiff = differenceInCalendarDays(today, endDate);
        return daysDiff >= 0 && daysDiff <= 3;
    };

    const renderForm = () => {
        switch (activeForm) {
            case SUBSCRIPTION_ACTION_FORMS.RENEW:
                return (
                    <RenewForm
                        isDisabled={!isFreshlyExpired()}
                        subscriptionId={subscriptionId}
                        setIsNotificationDialogOpen={
                            setIsNotificationDialogOpen
                        }
                    />
                );

            case SUBSCRIPTION_ACTION_FORMS.CHANGE_PLAN:
                return (
                    <EditPlanForm
                        subscriptionId={subscriptionId}
                        planId={plan?.id}
                        setIsNotificationDialogOpen={
                            setIsNotificationDialogOpen
                        }
                    />
                );

            case SUBSCRIPTION_ACTION_FORMS.CHANGE_DURATION:
                return (
                    <EditDurationForm
                        startAt={startAt}
                        endAt={endAt}
                        subscriptionId={subscriptionId}
                    />
                );

            case SUBSCRIPTION_ACTION_FORMS.EDIT_TAGS:
                return (
                    <EditTagsForm
                        subscriptionId={subscriptionId}
                        tags={tags}
                        onSuccess={handlePopoverClose}
                    />
                );

            case SUBSCRIPTION_ACTION_FORMS.TERMINATE:
                return <TerminateForm subscriptionId={subscriptionId} />;
            default:
                return null;
        }
    };

    useEffect(() => {
        setAnchorEl(null);
    }, [plan]);

    return (
        <>
            <ButtonGroup
                variant="outlined"
                aria-label="Basic button group"
                sx={{
                    "& button": {
                        borderColor: "grey.300",
                    },
                    "& button:hover": {
                        borderColor: "grey.300",
                    },
                }}
            >
                <Tooltip title="Change plan">
                    <Button
                        disabled={!isActive}
                        onClick={(e) =>
                            handleOpen(e, SUBSCRIPTION_ACTION_FORMS.CHANGE_PLAN)
                        }
                    >
                        <Edit fontSize="small" />
                    </Button>
                </Tooltip>
                <Tooltip title="Change duration">
                    <Button
                        disabled={!isActive}
                        onClick={(e) =>
                            handleOpen(
                                e,
                                SUBSCRIPTION_ACTION_FORMS.CHANGE_DURATION,
                            )
                        }
                    >
                        <EditCalendarOutlined fontSize="small" />
                    </Button>
                </Tooltip>
                <Tooltip title="Edit tags">
                    <Button
                        onClick={(e) =>
                            handleOpen(e, SUBSCRIPTION_ACTION_FORMS.EDIT_TAGS)
                        }
                    >
                        <LocalOfferOutlined fontSize="small" />
                    </Button>
                </Tooltip>
                <Tooltip title="Terminate">
                    <Button
                        disabled={!isActive}
                        onClick={(e) =>
                            handleOpen(e, SUBSCRIPTION_ACTION_FORMS.TERMINATE)
                        }
                    >
                        <DeleteForeverOutlined fontSize="small" />
                    </Button>
                </Tooltip>
                <Tooltip title="Renew Subscription">
                    <Button
                        onClick={(e) =>
                            handleOpen(e, SUBSCRIPTION_ACTION_FORMS.RENEW)
                        }
                    >
                        <AutorenewOutlined fontSize="small" />
                    </Button>
                </Tooltip>
            </ButtonGroup>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
            >
                {renderForm()}
            </Popover>
        </>
    );
};

export default SubscriptionRowActions;
